/**
 * THE ONE SUBMIT WAY — ALWAYS SYNC-BATCH (Ori's ruling, 2026-07-26).
 *
 * There is exactly ONE announce chunker in the workspace and it has NO size
 * branch: every submit leaves its producer sync-batch-wise (`serial` /
 * `sync_machine` / `sync_total` / `batch_status`), `total=1` when the payload
 * fits and slicing purely as transport when it does not. One shape on the wire
 * regardless of size — so the sync/batch crossing is what EVERY drive
 * exercises, not only a payload over the page bound. The second hand-written
 * copy (the onboarding harness' `mapSubmitChunks`) is DEAD; both the client
 * channel and the onboarding harness page through this owner.
 *
 * Why paging exists at all: a large confirmation lane cannot ride ONE announce
 * over the size-limited hops (client→outlet, outlet→world), nor be read back
 * within the world response cap. The announcement is still ONE (one machine):
 * the pages SHARE the sync machine and declare `sync.total`, the world keys
 * each page's input announcement by its content fingerprint (distinct rows, no
 * supersession), unions their lane CLAIMS, and the sync completion fires the
 * crossing ONCE from whichever page fills the last gap. Canonical batch
 * status: non-terminal pages "start", the terminal page "complete".
 */
export const ANNOUNCE_PAGE_BYTES = 1_500_000;

export type AnnounceArgs = Record<string, unknown>;

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  (value && typeof value === "object" && !Array.isArray(value))
    ? value as Record<string, unknown>
    : undefined;

const jsonSize = (value: unknown): number => JSON.stringify(value ?? null).length;

/** The sync machine EVERY page of one announce shares — the announcement's own
 * machine (the artifact shell's first, then the announce's). Never invented: an
 * announce carrying no machine id fails LOUD here instead of accumulating under
 * a made-up sync machine. */
const syncMachineOf = (
  announce: AnnounceArgs,
  artifact0: Record<string, unknown> | undefined,
): string => {
  const id = String(asRecord(artifact0?.machine)?.id || asRecord(announce.machine)?.id || "");
  if (!id) {
    throw new Error("announce batch requires the announcement machine id — sync:@machine is never invented");
  }
  return id;
};

/** Page ONE announce into its sync batch: the whole announce as a single
 * `total=1` page when it fits, byte-bounded confirmation-lane slices when it
 * does not. Every page carries the batch declaration. */
export function announceBatchPages(announce: AnnounceArgs): AnnounceArgs[] {
  const items = Array.isArray(announce.confirmation) ? announce.confirmation as unknown[] : [];
  const artifact0 = Array.isArray(announce.artifact) ? asRecord(announce.artifact[0]) : undefined;
  const artifactInput = asRecord(artifact0?.input);
  const inputMaps = Array.isArray(artifactInput?.maps) ? artifactInput.maps as unknown[] : [];
  const syncMachine = syncMachineOf(announce, artifact0);
  const ranges: Array<[number, number]> = [];
  if (items.length <= 1 || jsonSize(announce) <= ANNOUNCE_PAGE_BYTES) {
    ranges.push([0, items.length]);
  } else {
    let start = 0;
    let size = 0;
    for (let i = 0; i < items.length; i += 1) {
      const itemBytes = jsonSize(items[i]) + jsonSize(inputMaps[i] ?? {});
      if (i > start && size + itemBytes > ANNOUNCE_PAGE_BYTES) {
        ranges.push([start, i]);
        start = i;
        size = 0;
      }
      size += itemBytes;
    }
    ranges.push([start, items.length]);
  }
  const total = ranges.length;
  return ranges.map(([from, to], index) => ({
    ...announce,
    ...(items.length ? { confirmation: items.slice(from, to) } : {}),
    // the confirmation lane is the authoritative claim lane the world
    // accumulates. The artifact box is ONE (it supersedes to the last page), so
    // a paged copy of `input.maps` would leave the box carrying only the last
    // slice while the lane unions all — an aggregate inconsistency that skews
    // connect's map currentness. A SLICED announce therefore carries no map
    // copy on its box; the claims ride the lane alone. A single page IS the
    // whole lane, so its box is untouched.
    ...(total > 1 && artifact0 ? { artifact: [{ ...artifact0, input: { ...artifactInput, maps: [] } }] } : {}),
    serial: index + 1,
    sync_machine: syncMachine,
    sync_total: total,
    batch_status: index === total - 1 ? "complete" : "start",
  }));
}
